import { Outlet, useLocation, useNavigate } from "react-router";
import { useGetCourseQuery } from "./entities/course";
import { useUserInfoQuery } from "./entities/session";
import { useEffect } from "react";
import { useAuthGuard, useYandexMetrika } from "./shared/lib";
import { AccessRevokedOverlay, CookieBanner } from "./shared/ui";
import { SsoGate } from "./widgets/sso-gate";

// На курсе с SSO своих форм входа нет — вход через id.skillslab.center.
const SSO_AUTH_PATHS = ["/login", "/register", "/forgot-password"];

// Установка пароля по ссылке: публичная, без SsoGate и без запроса user_info
// (токен в ссылке — одноразовый токен users_service, а не токен курса).
const SET_PASSWORD_PATH = "/set_password";

// Хук для установки meta-тегов на клиенте
const useDocumentMeta = () => {
  const { data: course } = useGetCourseQuery();

  useEffect(() => {
    if (course) {
      // Устанавливаем title
      document.title = course.name || "My Course Platform";

      // Устанавливаем meta description
      const descriptionMeta = document.querySelector(
        'meta[name="description"]'
      );
      if (descriptionMeta) {
        descriptionMeta.setAttribute(
          "content",
          course.description || "Learn with our amazing courses"
        );
      } else {
        const meta = document.createElement("meta");
        meta.name = "description";
        meta.content = course.description || "Learn with our amazing courses";
        document.head.appendChild(meta);
      }

      // Устанавливаем favicon
      if (course.favicon) {
        const existingFavicon = document.querySelector("link[rel='icon']");
        if (existingFavicon) {
          existingFavicon.remove();
        }
        const link = document.createElement("link");
        link.rel = "icon";
        link.href = course.favicon;
        link.type = "image/png";
        document.head.appendChild(link);
      }

      // Open Graph теги
      updateOrCreateMeta("og:title", course.name);
      updateOrCreateMeta("og:description", course.description);
      updateOrCreateMeta("og:type", "website");

      // Twitter Card
      updateOrCreateMeta("twitter:card", "summary_large_image");
      updateOrCreateMeta("twitter:title", course.name);
      updateOrCreateMeta("twitter:description", course.description);

      // Apply custom colors from course settings
      if (course.colors) {
        const colorKeys = ["primary", "secondary", "success", "error"] as const;
        for (const key of colorKeys) {
          if (course.colors[key]) {
            document.documentElement.style.setProperty(
              `--color-${key}`,
              course.colors[key]
            );
          }
        }
      }
    }
  }, [course]);
};

// Вспомогательная функция для обновления meta-тегов
const updateOrCreateMeta = (nameOrProperty: string, content?: string) => {
  if (!content) return;

  const isProperty =
    nameOrProperty.startsWith("og:") || nameOrProperty.startsWith("twitter:");
  const selector = isProperty
    ? `meta[property="${nameOrProperty}"]`
    : `meta[name="${nameOrProperty}"]`;

  let meta = document.querySelector(selector);

  if (meta) {
    meta.setAttribute("content", content);
  } else {
    meta = document.createElement("meta");
    if (isProperty) {
      meta.setAttribute("property", nameOrProperty);
    } else {
      meta.setAttribute("name", nameOrProperty);
    }
    meta.setAttribute("content", content);
    document.head.appendChild(meta);
  }
};

export const Root = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: course } = useGetCourseQuery();
  const isSsoAuthPath =
    !!course?.sso_enabled && SSO_AUTH_PATHS.includes(location.pathname);

  // Устанавливаем meta-теги
  useDocumentMeta();

  // Подключаем счётчик Яндекс.Метрики (если задан у курса)
  useYandexMetrika();

  // Защита роутов - проверяем наличие токена на клиенте
  useAuthGuard();

  const isDevSimulatorGallery =
    import.meta.env.DEV && location.pathname === "/dev/simulator-blocks";

  const { data: user } = useUserInfoQuery(undefined, {
    skip:
      location.pathname.includes("/login") ||
      location.pathname === SET_PASSWORD_PATH ||
      location.pathname.includes("/register") ||
      isDevSimulatorGallery,
  });

  // Редирект на страницу персонажа если нет имени
  useEffect(() => {
    if (isDevSimulatorGallery) return;
    if (user) {
      if (!user?.first_name || !user?.last_name) {
        navigate("/character");
      }
    }
  }, [user, navigate, isDevSimulatorGallery]);

  return (
    <>
      {isSsoAuthPath ? <SsoGate /> : <Outlet />}
      <CookieBanner />
      <AccessRevokedOverlay />
    </>
  );
};

export default Root;
